import { ZfBanner, ZfStack } from '@zafire/react';
import type Highcharts from 'highcharts/highstock';
import type { FunctionComponent } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useProjection,
    useProjectionCashEventSettings,
    useProjectionCashFlowSettings,
    useProjectionExternalAssetSettings,
    useProjectionInternalAssetSettings,
    useSurplusSettings,
} from '../api';
import { useTotalSelectedBankableAssets } from '../components/exporter/scenarios/hooks';
import { MonteCarloChartSubHeaderWrapper } from '../components/projection/chart/MonteCarloChartSubHeaderWrapper';
import { MonteCarloChart } from '../components/projection/monteCarloChart/MonteCarloChart';
import { SimulationSettingsDrawer } from '../components/simulation-settings';
import { SETTINGS_DRAWER_WIDTH } from '../config/constants';
import { useLayout } from '../contexts/layout-context';
import { useMonteCarloSeries } from '../hooks/chart/useMonteCarloSeries';
import { useElementHeight } from '../hooks/useElementHeight';

export const ProjectionScreen: FunctionComponent = () => {
    const { t } = useTranslation();
    const { totalNavbarHeight } = useLayout();

    const { data: projection } = useProjection();
    const { isSuccess: isInternalAssetsSuccess } = useProjectionInternalAssetSettings();
    const { isSuccess: isExternalAssetsSuccess } = useProjectionExternalAssetSettings();
    const { isSuccess: isCashflowsSuccess } = useProjectionCashFlowSettings();
    const { isSuccess: isSurplusSuccess } = useSurplusSettings();
    const { isSuccess: isCashEventSuccess } = useProjectionCashEventSettings();
    const isSuccess = isInternalAssetsSuccess && isExternalAssetsSuccess && isCashflowsSuccess && isSurplusSuccess && isCashEventSuccess;

    const seriesData = useMonteCarloSeries();
    const totalSelectedBankableAssets = useTotalSelectedBankableAssets();

    const headerRef = useRef<HTMLDivElement>(null);
    const [chart, setChart] = useState<Highcharts.Chart | undefined>(undefined);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const headerHeight = useElementHeight(headerRef, [isDrawerOpen]);

    const handleOpenDrawer = () => setIsDrawerOpen(true);
    const handleCloseDrawer = () => setIsDrawerOpen(false);

    return (
        <div className="d-flex flex-1" style={{ width: '100%', height: `calc(100vh - ${totalNavbarHeight}px)`, overflow: 'hidden' }}>
            <ZfStack className="projection-screen__wrapper" direction="row" justify="flex-start" style={{ width: '100%', maxWidth: '100%', height: '100%' }}>
                <div
                    className="d-flex fd-column"
                    style={{ width: !isDrawerOpen ? '100%' : `calc(100% - ${SETTINGS_DRAWER_WIDTH}px)`, flexShrink: 1, minWidth: 0 }}
                >
                    <div className="projection-screen__header" ref={headerRef}>
                        {projection?.showWarning && (
                            <ZfBanner
                                style={{ width: '100%' }}
                                type="warning"
                                titleText={t('warningLoadMonteCarloProjectionTitle')}
                                description={t('warningLoadMonteCarloProjection')}
                                dismissible={false}
                            />
                        )}
                        <h3>{t('yourProjection')}</h3>
                        <MonteCarloChartSubHeaderWrapper
                            chart={chart}
                            totalSelectedBankableAssets={totalSelectedBankableAssets}
                            seriesData={seriesData}
                            isDrawerOpen={isDrawerOpen}
                            buttonDisabled={!isSuccess}
                            onOpenDrawer={() => (isDrawerOpen ? handleCloseDrawer() : handleOpenDrawer())}
                        />
                    </div>
                    <div
                        className="chart-container"
                        style={{
                            height: `calc(100vh - ${totalNavbarHeight + headerHeight}px)`,
                            width: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }}
                    >
                        <div className="chart-container__chart">
                            <MonteCarloChart isDrawerOpen={isDrawerOpen} setChart={setChart} seriesData={seriesData} />
                        </div>
                    </div>
                </div>
                <SimulationSettingsDrawer chartType="MONTE_CARLO_PROJECTION" isDrawerOpen={isDrawerOpen} onCloseDrawer={handleCloseDrawer} />
            </ZfStack>
        </div>
    );
};
